import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { saveAs } from 'file-saver';

class OrderedProjectionCsvRowMapper {
    constructor(projections) {
        this.projections = projections;
    }
    getColumnNames() {
        return this.projections.map(x => x[0]);
    }
    map(obj) {
        return this.projections.map(x => x[1](obj));
    }
}

class AutoCsvRowMapper {
    constructor(data) {
        if (!data || data.length === 0) {
            throw new Error('Parameter \'data\' must be a non-empty array of objects.');
        }
        if (typeof data[0] !== 'object') {
            throw new Error('Parameter \'data\' must contain objects.');
        }
        const projections = [];
        const allKeys = this.getAllKeys(data);
        for (const key of allKeys) {
            projections.push([
                key,
                this.createProjection(key)
            ]);
        }
        this.derivedCsvRowMapper = new OrderedProjectionCsvRowMapper(projections);
    }
    getColumnNames() {
        return this.derivedCsvRowMapper.getColumnNames();
    }
    map(obj) {
        return this.derivedCsvRowMapper.map(obj);
    }
    createProjection(property) {
        return (x) => {
            if (x.hasOwnProperty(property)) {
                const propertyValue = x[property];
                if (propertyValue === undefined || propertyValue === null) {
                    return '';
                }
                return propertyValue.toString();
            }
            else {
                return '';
            }
        };
    }
    getAllKeys(objs) {
        const allKeys = {};
        for (const obj of objs) {
            for (const objectKey of Object.keys(obj)) {
                allKeys[objectKey] = 1;
            }
        }
        return Object.keys(allKeys);
    }
}

class CsvConfiguration {
    constructor() {
        this.delimiter = ',';
        this.quote = '"';
        this.newLine = '\r\n';
        this.includeHeaderLine = true;
        this.outputValueForNull = '';
        this.outputValueForUndefined = '';
    }
}

class Ng2CsvService {
    download(data, filename, csvRowMapper, config = new CsvConfiguration()) {
        const csvData = this.convertToCsv(data, csvRowMapper, config);
        const mimeType = 'text/csv; header='
            + (config.includeHeaderLine ? 'present' : 'absent');
        const csvBlob = new Blob([csvData], { type: mimeType });
        saveAs(csvBlob, filename, true);
    }
    convertToCsv(data, csvRowMapper, config = new CsvConfiguration()) {
        if (csvRowMapper === undefined) {
            csvRowMapper = new AutoCsvRowMapper(data);
        }
        const rows = [];
        if (config.includeHeaderLine) {
            const headerFieldSeparator = `${config.quote}${config.delimiter}${config.quote}`;
            rows.push(config.quote
                + csvRowMapper.getColumnNames()
                    .map(x => this.escapeQuotes(x, config.quote))
                    .join(headerFieldSeparator)
                + config.quote);
        }
        for (const row of data) {
            rows.push(csvRowMapper.map(row)
                .map(x => this.mapNullOrUndefinedValues(x, config))
                .map(x => this.escapeRowValue(x, config))
                .join(config.delimiter));
        }
        return rows.join(config.newLine);
    }
    mapNullOrUndefinedValues(value, config) {
        switch (value) {
            case null: {
                return config.outputValueForNull;
            }
            case undefined: {
                return config.outputValueForUndefined;
            }
            default: {
                return value;
            }
        }
    }
    escapeQuotes(value, quoteChar) {
        return value.replace(new RegExp(quoteChar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), quoteChar + quoteChar);
    }
    escapeRowValue(value, config) {
        if (value.indexOf(config.delimiter) > -1
            || value.indexOf(config.quote) > -1) {
            const escapedValue = this.escapeQuotes(value, config.quote);
            return `${config.quote}${escapedValue}${config.quote}`;
        }
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class Ng2CsvModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.0", ngImport: i0, type: Ng2CsvModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

/*
 * Public API Surface of ng2csv
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AutoCsvRowMapper, CsvConfiguration, Ng2CsvModule, Ng2CsvService, OrderedProjectionCsvRowMapper };
